import React, { ReactNode, useContext } from 'react';
import useMultiFactorAuthenticationList from './useMultiFactorAuthenticationList';
import {
  EnforcementStatus,
  MultiFactorAuthentication,
  MultiFactorAuthenticationProviderOption,
} from '../../../Infrastructure/API/MultiFactorAuthentication/list';

interface MultiFactorAuthenticationContextProps
  extends Pick<ReturnType<typeof useMultiFactorAuthenticationList>, 'loading' | 'error' | 'fetched'> {
  authentications: MultiFactorAuthentication[];
  defaultAuthentication?: MultiFactorAuthentication;
  enforcementStatus?: EnforcementStatus;
  providerOptions: MultiFactorAuthenticationProviderOption[];
  authToken?: string;
}

const MultiFactorAuthenticationContext = React.createContext<MultiFactorAuthenticationContextProps>({
  authentications: [],
  defaultAuthentication: undefined,
  loading: false,
  error: false,
  fetched: false,
  providerOptions: [],
  authToken: undefined,
});

interface MultiFactorAuthenticationProviderProps {
  multiFactorAuthenticationList: ReturnType<typeof useMultiFactorAuthenticationList>;
  authToken?: string;
  children: ReactNode;
}

export const MultiFactorAuthenticationProvider = ({
  multiFactorAuthenticationList,
  authToken,
  children,
}: MultiFactorAuthenticationProviderProps) => {
  const { response, ...rest } = multiFactorAuthenticationList;

  return (
    <MultiFactorAuthenticationContext.Provider
      value={{
        authentications: response?.data ?? [],
        defaultAuthentication: response?.data.find((item) => item.id === response?.meta.default_id),
        enforcementStatus: response?.meta.enforcement_status,
        providerOptions: response?.meta.provider_options ?? [],
        authToken,
        ...rest,
      }}
    >
      {children}
    </MultiFactorAuthenticationContext.Provider>
  );
};

export function useMultiFactorAuthenticationContext() {
  return useContext(MultiFactorAuthenticationContext);
}
